import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Container,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { CaptivePortalTemplateProps } from '../types';
import { CaptivePortalContainer, isCaptivePortalFormDataValid } from './container';
import Link from '@shared/components/Link';

export const CompactTemplateDataKeys = [
  'logoUrl',
  'header',
  'content',
  'showName',
  'requireName',
  'showEmail',
  'requireEmail',
  'requireAcceptMarketing',
  'buttonText',
  'privacyPolicyUrl',
  'termsAndConditionsUrl',
  'fieldBorderColor',
  'fieldTextColor',
  'buttonColor',
  'buttonTextColor',
] as const;

export const CompactTemplate: React.FC<
  CaptivePortalTemplateProps<(typeof CompactTemplateDataKeys)[number]>
> = (props) => {
  const { data, formData, onFormChange, onSubmit, isSubmitting = false } = props;
  const { t } = useTranslation();

  const handleInputChange =
    (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onFormChange({
        ...formData,
        [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
      });
    };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isCaptivePortalFormDataValid(data, formData)) {
      onSubmit();
    }
  };

  return (
    <CaptivePortalContainer {...props}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Container maxWidth="xs">
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid #e5e7eb',
            }}
          >
            {data.logoUrl && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Box
                  component="img"
                  src={data.logoUrl}
                  alt="Logo"
                  sx={{
                    maxWidth: 100,
                    maxHeight: 60,
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}

            <Typography
              variant="h5"
              align="center"
              gutterBottom
              sx={{ fontWeight: 600, color: '#111827', mb: 1 }}
            >
              {data.header}
            </Typography>

            <Typography
              variant="body2"
              align="center"
              color="text.secondary"
              sx={{ mb: 3, fontSize: '0.875rem' }}
            >
              {data.content}
            </Typography>

            <Box component="form" onSubmit={handleFormSubmit} noValidate>
              {data.showName && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label={t('captivePortal.firstName', 'First Name')}
                    value={formData.firstName}
                    onChange={handleInputChange('firstName')}
                    required={data.requireName}
                    variant="outlined"
                    size="small"
                    sx={{
                      mb: 1.5,
                      '& .MuiInputLabel-root': {
                        color: data.fieldTextColor || 'text.primary',
                      },
                      '& .MuiInputBase-input': {
                        color: data.fieldTextColor || 'text.primary',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: data.fieldBorderColor || 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover fieldset': {
                          borderColor: data.fieldBorderColor || 'rgba(0, 0, 0, 0.87)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: data.buttonColor || 'primary.main',
                        },
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label={t('captivePortal.lastName', 'Last Name')}
                    value={formData.lastName}
                    onChange={handleInputChange('lastName')}
                    required={data.requireName}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              )}

              {data.showEmail && (
                <TextField
                  fullWidth
                  label={t('captivePortal.email', 'Email Address')}
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  required={data.requireEmail}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                />
              )}

              {data.termsAndConditionsUrl && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.acceptedTerms || false}
                      onChange={handleInputChange('acceptedTerms')}
                      size="small"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.23)',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {t('acceptTerms', 'I accept the')}{' '}
                      <Link
                        href={data.termsAndConditionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {t('termsConditions', 'Terms & Conditions')}
                      </Link>
                      {data.privacyPolicyUrl && (
                        <>
                          {' '}
                          {t('and', 'and')}{' '}
                          <Link
                            href={data.privacyPolicyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {t('privacyPolicy', 'Privacy Policy')}
                          </Link>
                        </>
                      )}
                      .
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />
              )}

              {data.requireAcceptMarketing && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.acceptedMarketing || false}
                      onChange={handleInputChange('acceptedMarketing')}
                      size="small"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.23)',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {t(
                        'acceptMarketing',
                        'I would like to receive marketing communications and promotional offers',
                      )}
                    </Typography>
                  }
                  sx={{ mb: 2, alignItems: 'flex-start' }}
                />
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="medium"
                disabled={isSubmitting || !isCaptivePortalFormDataValid(data, formData)}
                sx={{
                  py: 1.25,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  color: data.buttonTextColor || 'white',
                  backgroundColor: data.buttonColor || '#3b82f6',
                  '&:hover': {
                    backgroundColor: data.buttonColor || '#2563eb',
                  },
                  '&:disabled': {
                    opacity: 0.5,
                    backgroundColor: data.buttonColor || '#3b82f6',
                    color: data.buttonTextColor || 'white',
                  },
                }}
              >
                {isSubmitting
                  ? t('captivePortal.connecting', 'Connecting...')
                  : data.buttonText || t('captivePortal.connect', 'Connect')}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </CaptivePortalContainer>
  );
};
